import { HardDrive, Trash2 } from 'lucide-react';
import { useState } from 'react';

import { useEnvironmentList } from '@/react/portainer/environments/queries';
import { useGroups } from '@/react/portainer/environments/environment-groups/queries';

import { Datatable } from '@@/datatables';
import { createPersistedStore } from '@@/datatables/types';
import { AddButton, LoadingButton } from '@@/buttons';
import { useTableState } from '@@/datatables/useTableState';

import { isBE } from '../../feature-flags/feature-flags.service';
import {
  ENVIRONMENTS_POLLING_INTERVAL,
  isSortType,
} from '../queries/useEnvironmentList';
import { EnvironmentStatus } from '../types';

import { columns } from './columns';
import { EnvironmentListItem } from './types';
import { useIsRemovingEnvironments } from './useDeleteEnvironmentsMutation';

// Installs and KubeSolo removals show their progress in the list, so it refreshes faster while one runs.
const PROVISIONING_POLLING_INTERVAL = 5 * 1000;

const tableKey = 'environments';
const settingsStore = createPersistedStore(tableKey, 'Name');

export function EnvironmentsDatatable({
  onRemove,
}: {
  /** Resolves to whether the removal was confirmed */
  onRemove: (environments: Array<EnvironmentListItem>) => Promise<boolean>;
}) {
  const tableState = useTableState(settingsStore, tableKey);
  const isRemoving = useIsRemovingEnvironments();

  const [page, setPage] = useState(0);

  const groupsQuery = useGroups();
  const { environments, isLoading, totalCount } = useEnvironmentList(
    {
      search: tableState.search,
      excludeSnapshots: true,
      page: page + 1,
      pageLimit: tableState.pageSize,
      sort: isSortType(tableState.sortBy?.id)
        ? tableState.sortBy?.id
        : undefined,
      order: tableState.sortBy?.desc ? 'desc' : 'asc',
    },
    {
      enabled: groupsQuery.isSuccess,
      refetchInterval: (data) =>
        data?.value.some((e) => e.Status === EnvironmentStatus.Provisioning)
          ? PROVISIONING_POLLING_INTERVAL
          : ENVIRONMENTS_POLLING_INTERVAL,
    }
  );

  const environmentsWithGroups = environments.map<EnvironmentListItem>(
    (env) => {
      const groupId = env.GroupId;
      const group = groupsQuery.data?.find((g) => g.Id === groupId);
      return {
        ...env,
        GroupName: group?.Name,
      };
    }
  );

  return (
    <Datatable
      title="Environments"
      titleIcon={HardDrive}
      dataset={environmentsWithGroups}
      columns={columns}
      settingsManager={tableState}
      isServerSidePagination
      page={page}
      onPageChange={setPage}
      totalCount={totalCount}
      isLoading={isLoading}
      isRowSelectable={(row) =>
        row.original.Status !== EnvironmentStatus.Provisioning
      }
      renderTableActions={(selectedRows, clearSelection) => {
        // a selected row can turn Provisioning when a removal or install starts elsewhere
        const removableRows = selectedRows.filter(
          (row) => row.Status !== EnvironmentStatus.Provisioning
        );

        return (
          <div className="flex items-center gap-2">
            <LoadingButton
              type="button"
              color="dangerlight"
              disabled={removableRows.length === 0}
              isLoading={isRemoving}
              loadingText="Removing..."
              onClick={async () => {
                // the removed rows stay listed while KubeSolo is uninstalled, so they'd stay selected
                if (await onRemove(removableRows)) {
                  clearSelection();
                }
              }}
              icon={Trash2}
              className="!m-0"
              data-cy="remove-environments-button"
            >
              Remove
            </LoadingButton>

            {isBE && (
              <AddButton
                color="secondary"
                data-cy="environments-auto-onboarding-button"
                to="portainer.endpoints.edgeAutoCreateScript"
              >
                Auto onboarding
              </AddButton>
            )}

            <AddButton
              to="portainer.wizard.endpoints"
              params={{ referrer: 'environments' }}
              data-cy="environments-add-environments-button"
            >
              Add environment
            </AddButton>
          </div>
        );
      }}
      data-cy="environments-datatable"
    />
  );
}
